import {DOCUMENT} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {DirectBookingEntryParameters} from '../models/direct-booking-entry-parameters.model';
import {FormSaverPersistenceService} from './comeback-link/form-saver-persistence.service';
import {OrderFormFactoryService} from './comeback-link/order-form-factory.service';

const entryParameterNames = ['coupon', 'packageUuid'] as const;

@Injectable({providedIn: 'root'})
export class DirectBookingEntryParametersService {
  private readonly document = inject(DOCUMENT);
  private readonly orderFormFactory = inject(OrderFormFactoryService);
  private readonly persistence = inject(FormSaverPersistenceService);

  /**
   * Persists supported direct-booking entry parameters from the current browser URL.
   */
  public persistFromCurrentUrl(): void {
    const href = this.document.defaultView?.location.href;
    if (!href) {
      return;
    }

    this.persistFromUrl(href);
  }

  /**
   * Persists supported direct-booking entry parameters from a URL.
   *
   * @param url Absolute or application-relative URL to read.
   */
  public persistFromUrl(url: string): void {
    const parsedUrl = this.parseUrl(url);
    if (!parsedUrl) {
      return;
    }

    this.persistFromSearchParams(parsedUrl.searchParams);
  }

  /**
   * Restores locally stored direct-booking entry parameters.
   *
   * @returns Stored entry parameters, or `null` when none exist.
   */
  public restore(): DirectBookingEntryParameters | null {
    return this.persistence.restore()?.entryParameters ?? null;
  }

  /**
   * Parses absolute and router-relative URLs with the current browser origin as base.
   *
   * @param url URL to parse.
   * @returns Parsed URL, or `null` when the value is not parseable.
   */
  private parseUrl(url: string): URL | null {
    const baseUrl = this.document.defaultView?.location.origin ?? 'http://localhost';

    try {
      return new URL(url, baseUrl);
    } catch {
      return null;
    }
  }

  /**
   * Persists all supported entry parameters present in the provided search params.
   *
   * @param searchParams URL search params.
   */
  private persistFromSearchParams(searchParams: URLSearchParams): void {
    const nextParameters = this.restore() ?? {};
    let hasEntryParameter = false;

    entryParameterNames.forEach((parameterName) => {
      if (!searchParams.has(parameterName)) {
        return;
      }

      nextParameters[parameterName] = searchParams.get(parameterName) ?? '';
      hasEntryParameter = true;
    });

    if (!hasEntryParameter) {
      return;
    }

    this.persistToDraft(nextParameters);
  }

  /**
   * Persists entry parameters inside the central order form draft and active form state.
   *
   * @param entryParameters Direct-booking entry parameters to store.
   */
  private persistToDraft(entryParameters: DirectBookingEntryParameters): void {
    const draft = this.persistence.restore() ?? {};
    const nextDraft = {
      ...draft,
      entryParameters,
    };

    this.persistence.persistLocal(nextDraft);
    this.orderFormFactory.updateEntryParameters(entryParameters);
  }
}
